/**
 * Tableau de bord de la Direction (besoin 1, décisions 214 et 220) — une
 * synthèse en lecture seule : des comptes, des montants, des durées. Aucun
 * patient, aucun nom de personne.
 *
 * Chiffres neutres (décision 165, R7) : ni seuil, ni couleur, ni classement.
 * Le schéma est strict pour qu'un champ ajouté par mégarde côté serveur
 * échoue au test plutôt que de partir à l'écran.
 */
import { z } from 'zod';
/** Les passages se comptent sur 7 jours glissants, aujourd'hui compris, et les 7 d'avant. */
export const JOURS_PAR_SEMAINE_SYNTHESE = 7;
/**
 * Au-delà, l'attente n'est pas une attente (patient oublié, visite de la
 * veille) : écartée de la moyenne. Même borne que la performance des
 * praticiens (S316) — à unifier quand les deux branches seront mergées.
 */
export const ATTENTE_MAX_SYNTHESE_MINUTES = 240;
const jour = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** Un centre (agence ou antenne active) : ses passages, sa file, ses médecins. */
export const schemaCentreSynthese = z.strictObject({
    siteId: z.uuid(),
    siteNom: z.string(),
    passagesAujourdhui: z.number().int(),
    passagesSemaine: z.number().int(),
    passagesSemainePrecedente: z.number().int(),
    /** Arrivée → appel, des patients appelés aujourd'hui ; minutes, une décimale. */
    attenteMoyenneMin: z.number().nullable(),
    attentesMesurees: z.number().int(),
    /** Patients arrivés aujourd'hui et encore en file. */
    enFile: z.number().int(),
    /** Le plus ancien d'entre eux attend depuis… (minutes) ; `null` : file vide. */
    plusLongueAttenteMin: z.number().int().nullable(),
    /** Aujourd'hui ; `null` si l'activité des médecins est éteinte ou que personne n'a reçu. */
    medecins: z.number().int().nullable(),
    recusParMedecin: z.number().nullable(),
});
/** Le centre demandé (décision 249) : absent, le plus actif des 7 derniers jours. */
export const TOUS_LES_CENTRES = 'tous';
export const schemaFiltresSynthese = z.strictObject({
    /** Insensible à la casse, comme toute recherche du projet : « TOUS » vaut « tous ». */
    siteId: z
        .string()
        .trim()
        .toLowerCase()
        .pipe(z.union([z.literal(TOUS_LES_CENTRES), z.uuid()]))
        .optional(),
});
/** Une spécialité du centre sur les 7 derniers jours. */
export const schemaSpecialiteSynthese = z.strictObject({
    specialite: z.string(),
    passages: z.number().int(),
    /** Comme la colonne du centre ; `null` : rien de mesuré, ou activité des médecins éteinte. */
    attenteMoyenneMin: z.number().nullable(),
});
export const schemaSyntheseDirection = z.strictObject({
    /** Aujourd'hui à Madagascar. */
    jour,
    genereeLe: z.string(),
    /** Le centre de `passages` et `specialites` ; `null` : tous les centres. */
    siteId: z.uuid().nullable(),
    passages: z.strictObject({
        aujourdhui: z.number().int(),
        semaine: z.number().int(),
        semainePrecedente: z.number().int(),
        /** Les 14 derniers jours, du plus ancien à aujourd'hui, jours vides compris. */
        parJour: z.array(z.strictObject({ jour, nombre: z.number().int() })),
    }),
    /** Tous les centres actifs, quel que soit le centre choisi : la liste du sélecteur. */
    centres: z.array(schemaCentreSynthese),
    /** Les 7 derniers jours, du plus fréquenté au moins fréquenté. */
    specialites: z.array(schemaSpecialiteSynthese),
    /** `null` : la caisse est éteinte (FONCTION_CAISSE). Montants en ariary. */
    caisse: z
        .strictObject({
        encaisseAujourdhui: z.number().int(),
        /** Caisses d'un jour passé jamais clôturées (R13). */
        nonCloturees: z.number().int(),
        /** Reçus annulés après clôture, pas encore rendus. */
        aRendre: z.number().int(),
    })
        .nullable(),
    dns: z.strictObject({
        delaiAlerteJours: z.number().int(),
        /** Dernier DNS plus vieux que le délai d'alerte. */
        enRetard: z.number().int(),
        /** Aucun DNS appliqué. */
        sansDns: z.number().int(),
        /** Entreprises dont un DNS a été reçu ces 7 derniers jours. */
        recusSemaine: z.number().int(),
    }),
    couverture: z.strictObject({
        entreprises: z.number().int(),
        salaries: z.number().int(),
        ayantsDroit: z.number().int(),
        /** Ariary, deux décimales, en texte (décimal exact). */
        masseMensuelle: z.string(),
    }),
    aTraiter: z.strictObject({
        ecartsOffre: z.number().int(),
        fichesAConfirmer: z.number().int(),
        signalements: z.number().int(),
    }),
});
/** La journée d'un centre (maquette A, décision 249) : un centre, toujours. */
export const schemaFiltresJournee = z.strictObject({
    siteId: z.string().trim().toLowerCase().pipe(z.uuid()),
});
/** Les heures montrées commencent au plus tard à l'ouverture du centre. */
export const HEURE_OUVERTURE_CENTRE = 7;
export const schemaJourneeCentre = z.strictObject({
    jour,
    genereeLe: z.string(),
    siteId: z.uuid(),
    siteNom: z.string(),
    /** Entrées du jour, sans les annulations ; dont le paiement immédiat. */
    passages: z.number().int(),
    paiementImmediat: z.number().int(),
    /** Comme la synthèse : arrivée → appel, mêmes exclusions. */
    enFile: z.number().int(),
    plusLongueAttenteMin: z.number().int().nullable(),
    attenteMoyenneMin: z.number().nullable(),
    attentesMesurees: z.number().int(),
    /** Patients pris en charge aujourd'hui par un médecin. */
    recus: z.number().int(),
    /** `null` : activité des médecins éteinte, ou personne n'a reçu. */
    recusParMedecin: z.number().nullable(),
    /** Box en service du centre, et ceux qu'un médecin tient en ce moment. */
    boxTenus: z.number().int(),
    boxTotal: z.number().int(),
    /** Une ligne par heure (Madagascar), de l'ouverture à l'heure en cours, sans trou. */
    parHeure: z.array(z.strictObject({
        heure: z.number().int(),
        arrivees: z.number().int(),
        recus: z.number().int(),
    })),
    /**
     * L'attente des arrivés du jour, en minutes, par heure d'arrivée (Madagascar), de
     * l'arrivée à l'appel ; qui attend encore compte jusqu'à maintenant (`enCours`).
     * Seulement les heures où quelqu'un est arrivé.
     */
    attentesParHeure: z.array(z.strictObject({
        heure: z.number().int(),
        patients: z.number().int(),
        attenteMoyenneMin: z.number().int(),
        enCours: z.boolean(),
    })),
    /** Les mêmes patients, sur toute la journée ; `null` : personne. */
    attenteDuJourMin: z.number().int().nullable(),
    plusLongueAttenteDuJourMin: z.number().int().nullable(),
    /** Chaque spécialité en service ou attendue, de la plus longue file à la plus courte. */
    specialites: z.array(z.strictObject({
        specialite: z.string(),
        enFile: z.number().int(),
        plusLongueAttenteMin: z.number().int().nullable(),
        boxTenus: z.number().int(),
        boxTotal: z.number().int(),
    })),
    /** Sans nom de médecin ni de patient. */
    boxes: z.array(z.strictObject({
        id: z.uuid(),
        nom: z.string(),
        specialite: z.string(),
        tenu: z.boolean(),
        /** AJ-07 — un patient y est appelé, la consultation n'a pas encore commencé. */
        patientAppele: z.boolean(),
        /** Le patient en consultation y est depuis… (minutes) ; `null` : personne, ou pas encore entré. */
        consultationDepuisMin: z.number().int().nullable(),
    })),
});
/** La tendance d'un centre (maquette B, décisions 249 et 256) : 7 ou 30 jours complets, jusqu'à hier. */
export const JOURS_TENDANCE = [7, 30];
export const schemaFiltresTendance = z.strictObject({
    siteId: z.string().trim().toLowerCase().pipe(z.uuid()),
    /** « 7 » ou « 30 », écrits exactement ainsi (S363 : ni « 07 » ni « 7.0 ») ; 30 par défaut. */
    jours: z
        .enum(['7', '30'])
        .default('30')
        .transform((j) => (j === '7' ? 7 : 30)),
});
export const schemaJourTendance = z.strictObject({
    jour,
    /** Entrées du jour, sans les annulations. */
    passages: z.number().int(),
    /** Parmi elles, appelés au moins une fois sans réponse, ou notés absents. */
    sansReponse: z.number().int(),
    /** Comme la synthèse, par jour d'appel ; `null` : rien de mesuré. */
    attenteMoyenneMin: z.number().nullable(),
    attentesMesurees: z.number().int(),
});
const compteTendance = z.strictObject({ libelle: z.string(), nombre: z.number().int() });
export const schemaTendanceCentre = z.strictObject({
    jour,
    genereeLe: z.string(),
    siteId: z.uuid(),
    siteNom: z.string(),
    jours: z.number().int(),
    /** Deux fois `jours` jours complets, du plus ancien à hier : la période d'avant, puis la période (décision 256). */
    parJour: z.array(schemaJourTendance),
    /** Le reste porte sur la période seule. */
    prisEnCharge: z.number().int(),
    paiementImmediat: z.number().int(),
    specialites: z.array(schemaSpecialiteSynthese),
    offres: z.array(compteTendance),
    types: z.array(compteTendance),
    /** Les 6 entreprises qui viennent le plus, puis les autres réunies ; sans les passages sans entreprise. */
    entreprises: z.array(compteTendance),
    autresEntreprises: z.strictObject({ entreprises: z.number().int(), passages: z.number().int() }),
    /** `null` : activité des médecins éteinte, ou aucune durée retenue. */
    dureeMoyenneMin: z.number().nullable(),
});
//# sourceMappingURL=pilotage.js.map